import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../lib/axios';
import Header from '../components/layout/Header';
import RichTextEditor from '../components/email/RichTextEditor';
import AttachmentPicker from '../components/email/AttachmentPicker';
import AttachmentList from '../components/email/AttachmentList';
import LabelChip from '../components/email/LabelChip';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';

/** Convierte el *negrita* estilo WhatsApp que devuelve la IA a HTML simple. */
function whatsappBoldToHtml(text) {
    return (text || '')
        .replace(/\*(.+?)\*/g, '<strong>$1</strong>')
        .split('\n')
        .join('<br>');
}

/**
 * Quill nunca devuelve string vacío: con el editor en blanco emite
 * '<p><br></p>'. Sin esto, .trim() daba truthy y se podían mandar correos
 * con el cuerpo vacío.
 */
function isBlankHtml(html) {
    if (!html) return true;
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() === '';
}

// Mismo valor que ConnectedEmailAccount::SIGNATURE_MARKER en el backend: es
// lo que le permite detectar que la firma ya viene en el cuerpo y no
// duplicarla al enviar.
const SIGNATURE_MARKER = 'tornado-signature';

/**
 * Quita el bloque de firma para poder juzgar si el usuario escribió algo. Sin
 * esto, un cuerpo con SÓLO la firma precargada contaba como "no vacío" y
 * habilitaba el botón de enviar.
 */
function stripSignature(html) {
    return (html || '').replace(
        new RegExp(`<br>?\s*<div class="${SIGNATURE_MARKER}">[\s\S]*$`, 'i'),
        '',
    );
}

function signatureBlock(account) {
    const html = account?.signature_html;
    if (!html) return '';
    return `<br><div class="${SIGNATURE_MARKER}">${html}</div>`;
}

function buildFormData(fields, files) {
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (Array.isArray(value)) {
            value.forEach((v) => formData.append(`${key}[]`, v));
        } else {
            formData.append(key, value);
        }
    });
    files.forEach((file) => formData.append('attachments[]', file));
    return formData;
}

dayjs.extend(relativeTime);
dayjs.locale('es');

const FOLDERS = [
    { key: null, label: 'Todas', icon: 'inbox' },
    { key: 'INBOX', label: 'Recibidos', icon: 'move_to_inbox' },
    { key: 'SENT', label: 'Enviados', icon: 'send' },
    { key: 'SPAM', label: 'Spam', icon: 'report' },
    { key: 'TRASH', label: 'Papelera', icon: 'delete' },
];

export default function Emails() {
    const qc = useQueryClient();
    const [accountId, setAccountId] = useState(null);
    const [folder, setFolder] = useState(null);
    const [labelId, setLabelId] = useState(null);
    const [newLabelName, setNewLabelName] = useState('');
    const [selectedId, setSelectedId] = useState(null);
    const [replyBody, setReplyBody] = useState('');
    const [replyFiles, setReplyFiles] = useState([]);
    const [composeOpen, setComposeOpen] = useState(false);
    const [composeForm, setComposeForm] = useState({ account_id: '', to: '', subject: '', body_html: '' });
    const [composeFiles, setComposeFiles] = useState([]);

    const { data: accountsResp } = useQuery({
        queryKey: ['email-accounts'],
        queryFn: () => axios.get('/api/email-accounts'),
    });
    const accounts = accountsResp?.data || [];

    const { data: messagesResp, isLoading } = useQuery({
        queryKey: ['email-messages', accountId, folder, labelId],
        queryFn: () => axios.get('/api/email-messages', { params: { account_id: accountId || undefined, folder: folder || undefined, label_id: labelId || undefined, per_page: 50 } }),
    });
    const messages = messagesResp?.data?.data || [];

    const { data: labelsResp } = useQuery({
        queryKey: ['email-labels'],
        queryFn: () => axios.get('/api/email-labels'),
    });
    const labels = labelsResp?.data || [];

    const { data: selectedResp } = useQuery({
        queryKey: ['email-message', selectedId],
        queryFn: () => axios.get(`/api/email-messages/${selectedId}`),
        enabled: !!selectedId,
    });

    const selected = selectedResp?.data;

    // Abrir un correo lo marca leído en el servidor; sin esto la fila seguía
    // en negrita como si no se hubiera leído.
    useEffect(() => {
        if (selected?.email?.id) qc.invalidateQueries(['email-messages']);
    }, [selected?.email?.id]);

    // Al cambiar de correo hay que descartar el borrador: si no, el texto y los
    // adjuntos escritos para A se enviaban como respuesta a B. De paso se
    // precarga la firma de la cuenta que recibió ese correo, para que el
    // usuario la vea mientras escribe y pueda retocarla.
    useEffect(() => {
        setReplyBody(signatureBlock(selected?.email?.account));
        setReplyFiles([]);
    }, [selectedId, selected?.email?.account?.id]);

    const threads = useMemo(() => {
        const map = new Map();
        for (const m of messages) {
            const key = m.thread_id || `msg-${m.id}`;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(m);
        }
        return Array.from(map.entries()).map(([threadId, msgs]) => ({
            threadId,
            latest: msgs[0],
            count: msgs.length,
        }));
    }, [messages]);

    const syncMutation = useMutation({
        mutationFn: () => Promise.allSettled(accounts.map((a) => axios.post(`/api/email-accounts/${a.id}/sync`))),
        // allSettled y no all: con Promise.all, una sola cuenta con la clave
        // vencida rechazaba todo y la bandeja no se refrescaba ni mostraba
        // error, aunque las otras cuentas sí hubieran traído correo nuevo.
        onSuccess: (results) => {
            qc.invalidateQueries(['email-messages']);
            const fallidas = results.filter((r) => r.status === 'rejected').length;
            if (fallidas > 0) alert(`No se pudo sincronizar ${fallidas} de ${results.length} cuentas. Revisá sus credenciales en Configuración.`);
        },
    });

    const replyMutation = useMutation({
        mutationFn: () => axios.post(
            `/api/email-messages/${selectedId}/reply`,
            buildFormData({ body_html: replyBody }, replyFiles),
            { headers: { 'Content-Type': 'multipart/form-data' } },
        ),
        onSuccess: () => {
            setReplyBody('');
            setReplyFiles([]);
            qc.invalidateQueries(['email-messages']);
        },
        onError: (error) => alert(error.response?.data?.message || 'No se pudo enviar la respuesta.'),
    });

    const draftMutation = useMutation({
        mutationFn: () => axios.post(`/api/email-messages/${selectedId}/draft`),
        // La IA devuelve sólo el texto: se le vuelve a pegar la firma, que si no
        // quedaba pisada al reemplazar todo el cuerpo del editor.
        onSuccess: (response) => setReplyBody(
            whatsappBoldToHtml(response.data.response) + signatureBlock(selected?.email?.account)
        ),
        onError: (error) => alert(error.response?.data?.message || 'No se pudo generar un borrador con IA.'),
    });

    const moveMutation = useMutation({
        mutationFn: ({ id, folder: targetFolder }) => axios.post(`/api/email-messages/${id}/move`, { folder: targetFolder }),
        onSuccess: () => {
            setSelectedId(null);
            qc.invalidateQueries(['email-messages']);
        },
        onError: (error) => alert(error.response?.data?.message || 'No se pudo mover el correo.'),
    });

    const deleteForeverMutation = useMutation({
        mutationFn: (id) => axios.delete(`/api/email-messages/${id}`),
        onSuccess: () => {
            setSelectedId(null);
            qc.invalidateQueries(['email-messages']);
        },
        onError: (error) => alert(error.response?.data?.message || 'No se pudo eliminar el correo.'),
    });

    const createLabelMutation = useMutation({
        mutationFn: (name) => axios.post('/api/email-labels', { name }),
        onSuccess: () => {
            setNewLabelName('');
            qc.invalidateQueries(['email-labels']);
        },
        onError: (error) => alert(error.response?.data?.message || 'No se pudo crear la etiqueta.'),
    });

    const deleteLabelMutation = useMutation({
        mutationFn: (id) => axios.delete(`/api/email-labels/${id}`),
        onSuccess: (_, id) => {
            if (labelId === id) setLabelId(null);
            qc.invalidateQueries(['email-labels']);
            qc.invalidateQueries(['email-messages']);
            qc.invalidateQueries(['email-message']);
        },
    });

    const attachLabelMutation = useMutation({
        mutationFn: ({ emailId, labelId: id }) => axios.post(`/api/email-messages/${emailId}/labels`, { label_id: id }),
        onSuccess: () => {
            qc.invalidateQueries(['email-message', selectedId]);
            qc.invalidateQueries(['email-messages']);
        },
    });

    const detachLabelMutation = useMutation({
        mutationFn: ({ emailId, labelId: id }) => axios.delete(`/api/email-messages/${emailId}/labels/${id}`),
        onSuccess: () => {
            qc.invalidateQueries(['email-message', selectedId]);
            qc.invalidateQueries(['email-messages']);
        },
    });

    const composeMutation = useMutation({
        mutationFn: () => axios.post(
            '/api/email-messages',
            buildFormData({
                account_id: composeForm.account_id || undefined,
                to: composeForm.to.split(',').map((s) => s.trim()).filter(Boolean),
                subject: composeForm.subject,
                body_html: composeForm.body_html,
            }, composeFiles),
            { headers: { 'Content-Type': 'multipart/form-data' } },
        ),
        onSuccess: () => {
            setComposeOpen(false);
            setComposeForm({ account_id: '', to: '', subject: '', body_html: '' });
            setComposeFiles([]);
            qc.invalidateQueries(['email-messages']);
        },
        onError: (error) => alert(error.response?.data?.message || 'No se pudo enviar el correo.'),
    });

    return (
        <div className="flex flex-col h-screen bg-background-light dark:bg-background-dark">
            <Header title="Emails" />

            <div className="flex flex-1 overflow-hidden">
                {/* Sidebar: cuentas y carpetas */}
                <aside className="w-64 shrink-0 border-r border-gray-200 dark:border-gray-800 flex flex-col p-4 gap-4 overflow-y-auto">
                    <button
                        onClick={() => setComposeOpen(true)}
                        className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-xl flex items-center justify-center gap-2"
                    >
                        <span className="material-symbols-outlined text-lg">edit</span> Redactar
                    </button>

                    <button
                        onClick={() => syncMutation.mutate()}
                        disabled={syncMutation.isPending || accounts.length === 0}
                        className="w-full py-2 px-4 border border-gray-200 dark:border-gray-700 text-sm font-medium rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        <span className={`material-symbols-outlined text-lg ${syncMutation.isPending ? 'animate-spin' : ''}`}>sync</span>
                        Sincronizar
                    </button>

                    <div>
                        <p className="text-xs font-bold text-gray-400 uppercase mb-2">Carpetas</p>
                        {FOLDERS.map((f) => (
                            <button
                                key={f.key ?? 'all'}
                                onClick={() => setFolder(f.key)}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${folder === f.key ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 font-bold' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                            >
                                <span className="material-symbols-outlined text-lg">{f.icon}</span> {f.label}
                            </button>
                        ))}
                    </div>

                    <div>
                        <p className="text-xs font-bold text-gray-400 uppercase mb-2">Cuentas</p>
                        <button
                            onClick={() => setAccountId(null)}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm ${!accountId ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 font-bold' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                        >
                            Todas
                        </button>
                        {accounts.map((a) => (
                            <button
                                key={a.id}
                                onClick={() => setAccountId(a.id)}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm truncate ${accountId === a.id ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 font-bold' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                                title={a.email_address}
                            >
                                {a.display_name || a.email_address}
                            </button>
                        ))}
                        {accounts.length === 0 && (
                            <p className="text-xs text-gray-400 px-3">No hay cuentas conectadas. Agregá una en Configuración.</p>
                        )}
                    </div>

                    <div>
                        <p className="text-xs font-bold text-gray-400 uppercase mb-2">Etiquetas</p>
                        <div className="space-y-1">
                            {labels.map((l) => (
                                <div key={l.id} className="flex items-center justify-between group px-1">
                                    <button
                                        onClick={() => setLabelId(labelId === l.id ? null : l.id)}
                                        className="flex-1 text-left py-1"
                                    >
                                        <LabelChip label={l} active={labelId === l.id} />
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (window.confirm(`¿Eliminar la etiqueta "${l.name}"? Se quita de todos los correos.`)) deleteLabelMutation.mutate(l.id);
                                        }}
                                        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 shrink-0"
                                    >
                                        <span className="material-symbols-outlined text-sm">delete</span>
                                    </button>
                                </div>
                            ))}
                        </div>
                        <form
                            onSubmit={(e) => { e.preventDefault(); if (newLabelName.trim()) createLabelMutation.mutate(newLabelName.trim()); }}
                            className="flex items-center gap-1 mt-2"
                        >
                            <input
                                type="text"
                                value={newLabelName}
                                onChange={(e) => setNewLabelName(e.target.value)}
                                placeholder="Nueva etiqueta"
                                className="flex-1 min-w-0 text-xs px-2 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white"
                            />
                            <button type="submit" disabled={!newLabelName.trim() || createLabelMutation.isPending}
                                className="p-1.5 text-blue-600 disabled:opacity-40 shrink-0">
                                <span className="material-symbols-outlined text-lg">add_circle</span>
                            </button>
                        </form>
                    </div>
                </aside>

                {/* Lista de hilos */}
                <div className="w-96 shrink-0 border-r border-gray-200 dark:border-gray-800 overflow-y-auto">
                    {isLoading ? (
                        <div className="p-6 text-sm text-gray-400">Cargando...</div>
                    ) : threads.length === 0 ? (
                        <div className="p-6 text-sm text-gray-400">No hay correos.</div>
                    ) : (
                        threads.map(({ threadId, latest, count }) => (
                            <button
                                key={threadId}
                                onClick={() => setSelectedId(latest.id)}
                                className={`w-full text-left p-4 border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 ${selected?.email?.id === latest.id ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''} ${!latest.is_read ? 'font-bold' : ''}`}
                            >
                                <div className="flex justify-between items-baseline">
                                    <p className="text-sm truncate flex-1 dark:text-white">{latest.is_inbound ? latest.from_email : latest.to_email}</p>
                                    <span className="text-[11px] text-gray-400 shrink-0 ml-2">{dayjs(latest.date).fromNow()}</span>
                                </div>
                                <p className="text-sm truncate dark:text-gray-200">{latest.subject || '(Sin asunto)'} {count > 1 && <span className="text-gray-400 font-normal">({count})</span>}</p>
                                <p className="text-xs text-gray-400 truncate font-normal">{latest.snippet}</p>
                                {(latest.labels || []).length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {latest.labels.map((l) => <LabelChip key={l.id} label={l} />)}
                                    </div>
                                )}
                            </button>
                        ))
                    )}
                </div>

                {/* Detalle + responder */}
                <div className="flex-1 overflow-y-auto flex flex-col">
                    {!selected ? (
                        <div className="flex-1 flex items-center justify-center text-sm text-gray-400">Seleccioná un correo para verlo.</div>
                    ) : (
                        <>
                            <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-start justify-between gap-4">
                                <div>
                                    <h2 className="text-lg font-bold dark:text-white">{selected.email.subject || '(Sin asunto)'}</h2>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {selected.email.is_inbound ? `De: ${selected.email.from_email}` : `Para: ${selected.email.to_email}`}
                                        {' · '}{dayjs(selected.email.date).format('DD/MM/YYYY HH:mm')}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    {selected.email.folder === 'TRASH' ? (
                                        <>
                                            <button title="Restaurar a Recibidos" onClick={() => moveMutation.mutate({ id: selected.email.id, folder: 'INBOX' })}
                                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 rounded-lg">
                                                <span className="material-symbols-outlined text-lg">restore_from_trash</span>
                                            </button>
                                            <button title="Eliminar para siempre" onClick={() => {
                                                if (window.confirm('¿Eliminar este correo para siempre? No se puede deshacer.')) deleteForeverMutation.mutate(selected.email.id);
                                            }} className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 rounded-lg">
                                                <span className="material-symbols-outlined text-lg">delete_forever</span>
                                            </button>
                                        </>
                                    ) : selected.email.folder === 'SPAM' ? (
                                        <>
                                            <button title="No es spam" onClick={() => moveMutation.mutate({ id: selected.email.id, folder: 'INBOX' })}
                                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 rounded-lg">
                                                <span className="material-symbols-outlined text-lg">mark_email_read</span>
                                            </button>
                                            <button title="Eliminar" onClick={() => moveMutation.mutate({ id: selected.email.id, folder: 'TRASH' })}
                                                className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 rounded-lg">
                                                <span className="material-symbols-outlined text-lg">delete</span>
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button title="Marcar como spam" onClick={() => moveMutation.mutate({ id: selected.email.id, folder: 'SPAM' })}
                                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 rounded-lg">
                                                <span className="material-symbols-outlined text-lg">report</span>
                                            </button>
                                            <button title="Eliminar" onClick={() => moveMutation.mutate({ id: selected.email.id, folder: 'TRASH' })}
                                                className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 rounded-lg">
                                                <span className="material-symbols-outlined text-lg">delete</span>
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="px-6 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center flex-wrap gap-2">
                                {(selected.email.labels || []).map((l) => (
                                    <LabelChip key={l.id} label={l} onRemove={() => detachLabelMutation.mutate({ emailId: selected.email.id, labelId: l.id })} />
                                ))}
                                {labels.filter((l) => !(selected.email.labels || []).some((el) => el.id === l.id)).length > 0 && (
                                    <select
                                        value=""
                                        onChange={(e) => { if (e.target.value) attachLabelMutation.mutate({ emailId: selected.email.id, labelId: Number(e.target.value) }); }}
                                        className="text-xs px-2 py-1 border border-dashed border-gray-300 dark:border-gray-600 rounded-full text-gray-500 dark:bg-transparent"
                                    >
                                        <option value="">+ Etiqueta</option>
                                        {labels.filter((l) => !(selected.email.labels || []).some((el) => el.id === l.id)).map((l) => (
                                            <option key={l.id} value={l.id}>{l.name}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                            <div className="p-6 flex-1">
                                {selected.content?.html ? (
                                    <div className="prose dark:prose-invert max-w-none text-sm" dangerouslySetInnerHTML={{ __html: selected.content.html }} />
                                ) : (
                                    <p className="text-sm whitespace-pre-wrap dark:text-gray-200">{selected.content?.text || selected.email.snippet}</p>
                                )}
                                <AttachmentList attachments={selected.email.attachments} />
                            </div>
                            <div className="p-4 border-t border-gray-100 dark:border-gray-800 space-y-2">
                                <RichTextEditor
                                    value={replyBody}
                                    onChange={setReplyBody}
                                    placeholder="Escribí tu respuesta..."
                                />
                                <AttachmentPicker files={replyFiles} onChange={setReplyFiles} />
                                <div className="flex justify-between items-center pt-1">
                                    <button
                                        onClick={() => draftMutation.mutate()}
                                        disabled={draftMutation.isPending}
                                        className="py-2 px-4 border border-violet-200 dark:border-violet-800 text-violet-600 text-sm font-bold rounded-xl flex items-center gap-2 disabled:opacity-50"
                                    >
                                        <span className="material-symbols-outlined text-lg">auto_awesome</span>
                                        {draftMutation.isPending ? 'Generando...' : 'Generar con IA'}
                                    </button>
                                    <button
                                        onClick={() => replyMutation.mutate()}
                                        disabled={isBlankHtml(stripSignature(replyBody)) || replyMutation.isPending}
                                        className="py-2 px-6 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-xl disabled:opacity-50"
                                    >
                                        {replyMutation.isPending ? 'Enviando...' : 'Responder'}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {composeOpen && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-xl w-full max-w-3xl p-6 space-y-3 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center">
                            <h3 className="font-bold dark:text-white">Redactar correo</h3>
                            <button onClick={() => {
                                // Cerrar con la X también limpia: si no, al
                                // reabrir seguían cargados los adjuntos de la
                                // vez anterior, fuera de la vista del usuario, y
                                // se enviaban sin que los viera.
                                setComposeForm({ account_id: '', to: '', subject: '', body_html: '' });
                                setComposeFiles([]);
                                setComposeOpen(false);
                            }} className="text-gray-400 hover:text-gray-600">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <select value={composeForm.account_id} onChange={(e) => setComposeForm({ ...composeForm, account_id: e.target.value })}
                            className="w-full text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white">
                            <option value="">Cuenta predeterminada</option>
                            {accounts.map((a) => <option key={a.id} value={a.id}>{a.display_name || a.email_address}</option>)}
                        </select>
                        <input type="text" placeholder="Para (separados por coma)" value={composeForm.to}
                            onChange={(e) => setComposeForm({ ...composeForm, to: e.target.value })}
                            className="w-full text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white" />
                        <input type="text" placeholder="Asunto" value={composeForm.subject}
                            onChange={(e) => setComposeForm({ ...composeForm, subject: e.target.value })}
                            className="w-full text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white" />
                        <RichTextEditor
                            value={composeForm.body_html}
                            onChange={(html) => setComposeForm({ ...composeForm, body_html: html })}
                            placeholder="Mensaje"
                        />
                        <AttachmentPicker files={composeFiles} onChange={setComposeFiles} />
                        {(() => {
                            // En redactar no se precarga la firma en el editor: la
                            // cuenta puede cambiarse con el desplegable de arriba y
                            // habría que ir reemplazando el bloque a mano, con riesgo
                            // de duplicarla o borrar texto del usuario. Se avisa y la
                            // agrega el backend con la cuenta que finalmente se use.
                            const cuenta = composeForm.account_id
                                ? accounts.find((a) => String(a.id) === String(composeForm.account_id))
                                : accounts.find((a) => a.is_default) || accounts[0];
                            return cuenta?.signature_html ? (
                                <p className="text-xs text-gray-400 flex items-center gap-1">
                                    <span className="material-symbols-outlined text-sm">signature</span>
                                    Se agregará la firma de {cuenta.display_name || cuenta.email_address}.
                                </p>
                            ) : null;
                        })()}
                        <div className="flex justify-end pt-1">
                            <button
                                onClick={() => composeMutation.mutate()}
                                disabled={!composeForm.to || !composeForm.subject || isBlankHtml(composeForm.body_html) || composeMutation.isPending}
                                className="py-2 px-6 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-xl disabled:opacity-50"
                            >
                                {composeMutation.isPending ? 'Enviando...' : 'Enviar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
